import React from 'react';

import Debug from '@/components/Debug';
import { PRIOR_AUTH_OPTIONS } from '@/constants/treatability';
import useAuditLog from '@/hooks/useAuditLog';
import { CopyableField, Tooltip } from '@/ui';
import AuditLogLink from '@/ui/components/AuditLogLink';
import { currencyFormatter, currencyInCentsFormatter } from '@/utils/currencies';
import { formatNumericCalendarDate } from '@/utils/dates';

import './styles.css';
import type { Props } from './types';

const PRIOR_AUTH_LABELS: Record<string, string> = {
  [PRIOR_AUTH_OPTIONS.YES]: 'Required',
  [PRIOR_AUTH_OPTIONS.NO]: 'Not required',
  [PRIOR_AUTH_OPTIONS.UNKNOWN]: 'Unknown'
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div className="text-xs font-semibold uppercase tracking-wide text-uniform-gray-500">
    {children}
  </div>
);

const formatDate = (date: string | null | undefined) =>
  date ? formatNumericCalendarDate(date) : null;

const yesNo = (flag: boolean | undefined) => (flag == null ? null : flag ? 'Yes' : 'No');

const CardThumbnail = ({ label, url }: { label: string; url: string | null | undefined }) =>
  url ? (
    <Tooltip content={<img loading="lazy" src={url} alt={label} className="w-50" />}>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="flex h-24 w-40 flex-col items-center justify-center gap-1 rounded-md border border-uniform-gray-300 bg-uniform-gray-50 text-sm text-blue-700">
        {label}
      </a>
    </Tooltip>
  ) : (
    <div className="flex h-24 w-40 flex-col items-center justify-center gap-1 rounded-md border border-uniform-gray-200 bg-uniform-gray-50 text-sm text-uniform-gray-400">
      {`No ${label.toLowerCase()}`}
    </div>
  );

const PolicyDetailsList = ({ policy, className }: Props) => {
  const { data: auditLogData } = useAuditLog(
    { appLabel: 'insurance', model: 'insurancepolicy', objectId: policy.id ?? '' },
    !!policy.id
  );

  const insurerAddress =
    [
      policy.insurer_address_line_1,
      policy.insurer_address_line_2,
      policy.insurer_city,
      policy.insurer_state,
      policy.insurer_zip_code,
      policy.insurer_country
    ]
      .filter(Boolean)
      .join(', ') || null;

  const planDates =
    policy.plan_start_date || policy.plan_end_date
      ? `${formatDate(policy.plan_start_date) ?? '—'} – ${formatDate(policy.plan_end_date) ?? 'open'}`
      : null;

  const created = auditLogData?.data?.created;
  const lastUpdated = auditLogData?.data?.last_updated;

  return (
    <section
      aria-label="All policy details"
      className={`relative flex flex-col gap-5 text-sm ${className ?? ''}`}>
      <strong>All policy details</strong>

      <div className="flex flex-col gap-2">
        <SectionHeading>Identifiers</SectionHeading>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
          <CopyableField label="Policy ID" value={policy.id} mono />
          <CopyableField label="Subscriber ID" value={policy.subscriber_id} mono />
          <CopyableField label="Member ID" value={policy.policy_member_id} mono />
          <CopyableField label="Group ID" value={policy.group_id} />
          <CopyableField label="Health plan ID" value={policy.health_plan_id} />
          <CopyableField label="Healthie plan ID" value={policy.plan_healthie_id} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <SectionHeading>Policyholder</SectionHeading>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <CopyableField label="Relationship" value={policy.relationship} />
            <CopyableField label="Date of birth" value={formatDate(policy.policyholder_dob)} />
            <CopyableField label="First name" value={policy.policyholder_first_name} />
            <CopyableField label="Last name" value={policy.policyholder_last_name} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <SectionHeading>Benefits on the policy record</SectionHeading>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <CopyableField
              label="Annual deductible"
              value={
                policy.annual_deductible != null
                  ? currencyFormatter(policy.annual_deductible)
                  : null
              }
            />
            <CopyableField
              label="Out-of-pocket max"
              value={
                policy.out_of_pocket_maximum != null
                  ? currencyInCentsFormatter(policy.out_of_pocket_maximum)
                  : null
              }
            />
            <CopyableField
              label="Co-pay"
              value={
                policy.co_pay != null
                  ? `${currencyInCentsFormatter(policy.co_pay)}${policy.co_pay_unit ? ` ${policy.co_pay_unit}` : ''}`
                  : null
              }
            />
            <CopyableField
              label="Co-insurance"
              value={
                policy.co_insurance != null ? `${Math.round(policy.co_insurance * 100)}%` : null
              }
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <SectionHeading>Coverage and status</SectionHeading>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <CopyableField label="Carrier" value={policy.carrier} />
            <CopyableField label="Plan" value={policy.plan} />
            <CopyableField
              label="Type · Order"
              value={[policy.type, policy.priority_type].filter(Boolean).join(' · ') || null}
            />
            <CopyableField label="Plan dates" value={planDates} />
            <CopyableField label="In network" value={yesNo(policy.is_in_network)} />
            <CopyableField
              label="Prior auth"
              value={
                policy.prior_auth_required ? PRIOR_AUTH_LABELS[policy.prior_auth_required] : null
              }
            />
            <CopyableField label="Found by discovery" value={yesNo(policy.was_discovered)} />
            <CopyableField label="Guarantor" value={policy.guarantor} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <SectionHeading>Insurer and pharmacy</SectionHeading>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <CopyableField label="Insurer address" value={insurerAddress} className="col-span-2" />
            <CopyableField label="Provider phone" value={policy.provider_phone_number} />
            <CopyableField
              label="Rx BIN · Rx group"
              value={[policy.rx_bin, policy.rx_group].filter(Boolean).join(' · ') || null}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <CardThumbnail label="Front of card" url={policy.insurance_card_images?.front_image_url} />
        <CardThumbnail label="Back of card" url={policy.insurance_card_images?.back_image_url} />
        {policy.insurance_card_images?.id && (
          <AuditLogLink
            entityType="insurancecardimage"
            entityId={policy.insurance_card_images.id}
            className="font-medium">
            Card image history
          </AuditLogLink>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-uniform-gray-100 pt-3 text-uniform-gray-500">
        {created && (
          <span>
            Created by{' '}
            <strong className="text-uniform-gray-700">{created.actor?.email ?? 'System'}</strong>
            {` · ${new Date(created.timestamp).toLocaleDateString('en-US')}`}
          </span>
        )}
        {lastUpdated && (
          <span>
            Last updated by{' '}
            <strong className="text-uniform-gray-700">
              {lastUpdated.actor?.email ?? 'System'}
            </strong>
            {` · ${new Date(lastUpdated.timestamp).toLocaleDateString('en-US')}`}
          </span>
        )}
      </div>
      <Debug object={policy} />
    </section>
  );
};

export default React.memo(PolicyDetailsList);
